import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AlertsPanel = ({ alerts = [] }) => {
  const [expandedAlert, setExpandedAlert] = useState(null);

  const getAlertIcon = (type) => {
    switch (type) {
      case 'emergency': return 'AlertTriangle';
      case 'warning': return 'AlertCircle';
      case 'info': return 'Info';
      case 'success': return 'CheckCircle';
      default: return 'Bell';
    }
  };

  const getAlertColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-error bg-error/10 border-error/20';
      case 'medium': return 'text-warning bg-warning/10 border-warning/20';
      case 'low': return 'text-muted-foreground bg-muted/30 border-border';
      default: return 'text-foreground bg-surface border-border';
    }
  };

  const getPriorityBadge = (priority) => {
    const colors = {
      high: 'bg-error text-error-foreground',
      medium: 'bg-warning text-warning-foreground',
      low: 'bg-muted text-muted-foreground'
    };
    return colors?.[priority] || colors?.low;
  };

  const handleAlertAction = (alertId, action) => {
    console.log(`Alert ${alertId}: ${action}`);
    // Handle alert actions (acknowledge, dismiss, etc.)
  };

  const toggleExpanded = (alertId) => {
    setExpandedAlert(expandedAlert === alertId ? null : alertId);
  };

  if (alerts?.length === 0) {
    return (
      <div className="bg-surface border border-border rounded-xl p-6 shadow-md">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-foreground">Alerts & Notifications</h2>
          <div className="w-8 h-8 bg-success/10 rounded-full flex items-center justify-center">
            <Icon name="CheckCircle" size={16} className="text-success" />
          </div>
        </div>
        <div className="text-center py-8">
          <Icon name="Shield" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">All systems running smoothly</p>
          <p className="text-sm text-muted-foreground mt-1">No active alerts or notifications</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface border border-border rounded-xl p-6 shadow-md">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Alerts & Notifications</h2>
          <p className="text-sm text-muted-foreground">
            {alerts?.length} active alert{alerts?.length > 1 ? 's' : ''}
          </p>
        </div>
        <Button variant="ghost" size="sm" className="breathing-focus">
          <Icon name="Settings" size={16} className="mr-2" />
          Settings
        </Button>
      </div>
      <div className="space-y-3 max-h-96 overflow-y-auto">
        {alerts?.map((alert) => (
          <div
            key={alert?.id}
            className={`border rounded-lg p-4 transition-all duration-200 ${getAlertColor(alert?.priority)}`}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3 flex-1">
                <Icon 
                  name={getAlertIcon(alert?.type)} 
                  size={20} 
                  className={alert?.priority === 'high' ? 'text-error' : 
                           alert?.priority === 'medium' ? 'text-warning' : 'text-muted-foreground'} 
                />
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <h4 className="font-medium text-foreground">{alert?.title}</h4>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityBadge(alert?.priority)}`}>
                      {alert?.priority}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">{alert?.message}</p>
                  
                  {expandedAlert === alert?.id && alert?.details && (
                    <div className="mt-3 p-3 bg-muted/30 rounded-lg">
                      <p className="text-sm text-foreground">{alert?.details}</p>
                      {alert?.recommendations && (
                        <div className="mt-2">
                          <p className="text-xs font-medium text-muted-foreground mb-1">Recommendations:</p>
                          <ul className="text-xs text-muted-foreground space-y-1">
                            {alert?.recommendations?.map((rec, index) => (
                              <li key={index} className="flex items-start space-x-1">
                                <span>•</span>
                                <span>{rec}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex items-center justify-between mt-3">
                    <span className="text-xs text-muted-foreground">
                      {alert?.timestamp} • Client: {alert?.clientName}
                    </span>
                    <div className="flex space-x-2">
                      {alert?.details && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleExpanded(alert?.id)}
                          className="text-xs"
                        >
                          {expandedAlert === alert?.id ? 'Less' : 'More'}
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleAlertAction(alert?.id, 'acknowledge')}
                        className="text-xs"
                      >
                        Acknowledge
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {alerts?.length > 3 && (
        <div className="mt-4 pt-4 border-t border-border text-center">
          <Button variant="outline" size="sm" className="breathing-focus">
            View All Alerts
          </Button>
        </div>
      )}
    </div>
  );
};

export default AlertsPanel;